import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import { useUserInformationContext } from 'contexts/userContext';
import { usePreferences } from 'contexts/preferences-context';
import { useWorkspaceContext } from 'contexts/workspace-context';
import { TabOrder } from 'hooks/useOrderTabs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import querys from 'services/querys';
import { LEDGER_CODE_PURCHASE_PRICE } from 'utils/constants';

export default function useOrdersLayout() {
  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { orderMenuCollapsed, isReady, setOrderMenuCollapsed } = usePreferences() as any;
  const [sidemenuOpen, setSidemenuOpen] = useState(!orderMenuCollapsed);

  const { id: orderId } = useParams<{ id: string }>();

  const { addOrderTab } = useWorkspaceContext();
  const { llmServiceRef, isServiceReady } = useLLMProxyServiceContext();
  const { currentUser } = useUserInformationContext();

  const getGraphOrder = useQuery(['party-order', orderId], () => querys.getOrder(orderId), {
    enabled: !!orderId,
    refetchOnWindowFocus: false
  });

  const handleAddOrderTab = useCallback((orderTab: TabOrder) => {
    addOrderTab(orderTab);
  }, [addOrderTab]);

  useEffect(() => {
    if (isReady) {
      setSidemenuOpen(!orderMenuCollapsed);
    }
  }, [isReady, orderMenuCollapsed]);

  // Wrap setSidemenuOpen to also persist orderMenuCollapsed when ready
  const handleSetSidemenuOpen = useCallback((open: boolean) => {
    setSidemenuOpen(open);
    if (isReady) {
      setOrderMenuCollapsed(!open);
    }
  }, [isReady, setOrderMenuCollapsed]);

  useEffect(() => {
    if (getGraphOrder.data) {
      const { _id, order_id } = getGraphOrder.data.getOrder;

      const orderTab: TabOrder = {
        id: _id,
        orderId: order_id,
        firstPropertyAddress: '',
        lastSeen: 0,
      };

      handleAddOrderTab(orderTab);
    }
  }, [getGraphOrder.data, handleAddOrderTab, llmServiceRef]);

  // `isServiceReady` re-pushes the order after a hard reload, when the order loads before the AI service exists.
  useEffect(() => {
    if (!getGraphOrder.data) return undefined;

    const { _id, order_id, configuration } = getGraphOrder.data.getOrder;
    const service = llmServiceRef.current;
    service?.setActiveOrder({
      internalId: _id,
      orderNumber: order_id,
      orderType: configuration?.type,
      transactionType: configuration?.transaction_type,
    });

    return () => service?.setActiveOrder(null);
  }, [getGraphOrder.data, isServiceReady, llmServiceRef]);

  const orderType = getGraphOrder.data?.getOrder.configuration?.type || '';
  const transactionType = getGraphOrder.data?.getOrder.configuration?.transaction_type || '';
  const properties = getGraphOrder.data?.getOrder.estates || [];
  const parties = getGraphOrder.data?.getOrder.parties || [];
  const isOrderLoading = getGraphOrder.isLoading;
  const orderHasAccountingData = Boolean(getGraphOrder.data?.getOrder.accounting);
  const idTitleCompany =
		getGraphOrder.data?.getOrder.parties.find((party) => party.kinds.includes('Title Company'))
		  ?._id ?? '';

  return {
    t,
    url,
    history,
    sidemenuOpen,
    setSidemenuOpen: handleSetSidemenuOpen as any,
    properties,
    parties,
    isOrderLoading,
    statusFetchOrderData: getGraphOrder.status,
    orderHasAccountingData,
    orderType,
    idTitleCompany,
    transactionType,
    currentUser,
  };
}
